import { REGISTRATION_PAGE_ID, normalizePageTransitionId } from "./escapeRoomEditorConstants";
import { genId, defaultElement } from "./escapeRoomElementFactory";
import { getDefaultRegistrationPage } from "./defaultRegistrationPage";
import { clampEscapeRoomPlaybackRate } from "./escapeRoomVideoPlayback";

/** `quizId` en el elemento puede ser string u objeto `{ _id }` (populate / copias). */
function resolvePlayQuizId(rawQuizId) {
	if (!rawQuizId) return null;
	if (typeof rawQuizId === "string") {
		const t = rawQuizId.trim();
		return t && t !== "[object Object]" ? t : null;
	}
	if (typeof rawQuizId === "object") {
		if (rawQuizId._id) return String(rawQuizId._id);
		if (rawQuizId.id) return String(rawQuizId.id);
	}
	return null;
}

function normalizeQuizElementContent(e) {
	if (e?.type !== "quiz" || !e.content?.quizId) return e;
	const quizId = resolvePlayQuizId(e.content.quizId);
	if (!quizId || quizId === e.content.quizId) return e;
	return { ...e, content: { ...e.content, quizId } };
}

/** Normaliza quizId también en quizzes anidados en modales de botón/pista. */
function normalizeElementTreeQuizIds(e) {
	let next = normalizeQuizElementContent(e);
	const c = next?.content;
	if (!c || typeof c !== "object") return next;
	let contentChanged = false;
	let content = c;
	const patchModalDesign = (key) => {
		const design = content[key];
		if (!design || !Array.isArray(design.elements)) return;
		const els = design.elements.map(normalizeElementTreeQuizIds);
		const same = els.every((el, i) => el === design.elements[i]);
		if (same) return;
		if (!contentChanged) {
			content = { ...content };
			contentChanged = true;
		}
		content[key] = { ...design, elements: els };
	};
	if (next.type === "button") patchModalDesign("modalDesign");
	if (next.type === "hint") {
		patchModalDesign("hintModalDesign");
		patchModalDesign("confirmModalDesign");
	}
	return contentChanged ? { ...next, content } : next;
}

function collectUniqueQuizIdsFromElements(elements, into) {
	const ids = into || new Set();
	for (const e of elements || []) {
		if (e?.type === "quiz" && e.content?.quizId) {
			const id = resolvePlayQuizId(e.content.quizId);
			if (id) ids.add(id);
		}
		const c = e?.content;
		if (!c) continue;
		if (e.type === "button" && Array.isArray(c.modalDesign?.elements)) {
			collectUniqueQuizIdsFromElements(c.modalDesign.elements, ids);
		}
		if (e.type === "hint") {
			if (Array.isArray(c.hintModalDesign?.elements)) {
				collectUniqueQuizIdsFromElements(c.hintModalDesign.elements, ids);
			}
			if (Array.isArray(c.confirmModalDesign?.elements)) {
				collectUniqueQuizIdsFromElements(c.confirmModalDesign.elements, ids);
			}
		}
	}
	return into ? ids : [...ids];
}

function collectUniqueQuizIdsFromPages(pages) {
	const ids = new Set();
	for (const p of pages || []) {
		collectUniqueQuizIdsFromElements(p?.elements || [], ids);
	}
	return [...ids];
}

const elementOrNestedHasQuiz = (e) => {
	if (!e) return false;
	if (e.type === "quiz") return true;
	const c = e.content || {};
	if (e.type === "button" && (c.modalDesign?.elements || []).some(elementOrNestedHasQuiz)) return true;
	if (e.type === "hint") {
		if ((c.hintModalDesign?.elements || []).some(elementOrNestedHasQuiz)) return true;
		if ((c.confirmModalDesign?.elements || []).some(elementOrNestedHasQuiz)) return true;
	}
	return false;
};

const pageHasQuizOrPoints = (page) =>
	(page?.elements || []).some(elementOrNestedHasQuiz);

const normalizePages = (pages) => {
	const registration = getDefaultRegistrationPage();
	if (!pages || !Array.isArray(pages) || pages.length === 0) {
		return [registration];
	}
	const hasRegistration = pages.some((p) => (p.pageId || p._id) === REGISTRATION_PAGE_ID);
	let normalized = pages.map((p, i) => ({
		pageId: p.pageId || p._id || genId(),
		order: p.order ?? i,
		name: p.name || `Página ${i + 1}`,
		backgroundColor: p.backgroundColor,
		backgroundImageUrl: p.backgroundImageUrl,
		backgroundImageOpacity: p.backgroundImageOpacity,
		advanceOnBackgroundVideoEnd: !!p.advanceOnBackgroundVideoEnd,
		backgroundVideoPlaybackRate: clampEscapeRoomPlaybackRate(p.backgroundVideoPlaybackRate ?? 1),
		transitionToNext: normalizePageTransitionId(p.transitionToNext || "none"),
		transitionDuration: p.transitionDuration ?? 350,
		pointsOnLand: p.pointsOnLand ?? 0,
		timerRuns: p.timerRuns !== false,
		scenarioId: p.scenarioId ?? "",
		showFooter: p.showFooter ?? false,
		registrationDesign: (p.pageId || p._id) === REGISTRATION_PAGE_ID
			? (() => {
				const rd = { ...registration.registrationDesign, ...(p.registrationDesign || {}) };
				if (!rd.logoUrl && (rd.logoOncommitUrl || rd.logoJandJUrl)) {
					rd.logoUrl = rd.logoOncommitUrl || rd.logoJandJUrl;
				}
				delete rd.logoOncommitUrl;
				delete rd.logoJandJUrl;
				return rd;
			})()
			: undefined,
		elements: (p.elements || []).map((e) => normalizeElementTreeQuizIds({
			...e,
			elementId: e.elementId || e._id || genId(),
			entryAnimation: e.entryAnimation || "none",
			entryAnimationDuration:
				Number.isFinite(Number(e.entryAnimationDuration)) && Number(e.entryAnimationDuration) > 0
					? Number(e.entryAnimationDuration)
					: 500,
		})),
	}));
	if (!hasRegistration) {
		normalized = [registration, ...normalized.map((p, i) => ({ ...p, order: i + 1 }))];
	} else {
		const regPage = normalized.find((p) => (p.pageId || p._id) === REGISTRATION_PAGE_ID);
		const defaultReg = getDefaultRegistrationPage();
		const mergedReg = {
			...defaultReg,
			registrationDesign: { ...defaultReg.registrationDesign, ...(regPage.registrationDesign || {}) },
		};
		const oldLogoRoles = ["logoOncommit", "logoJandJ"];
		const hasOldLogos = (mergedReg.elements || []).some((e) => oldLogoRoles.includes(e.registrationRole));
		const hasNewLogo = (mergedReg.elements || []).some((e) => e.registrationRole === "logo");
		if (hasOldLogos && !hasNewLogo) {
			const logoEl = {
				...defaultElement("image"),
				elementId: genId(),
				x: 430, y: 8, width: 100, height: 100,
				content: { url: "", alt: "Logo" },
				registrationLocked: false,
				registrationRole: "logo",
			};
			mergedReg.elements = (mergedReg.elements || []).filter((e) => !oldLogoRoles.includes(e.registrationRole));
			mergedReg.elements.unshift(logoEl);
		}
		normalized = normalized.map((p) =>
			(p.pageId || p._id) === REGISTRATION_PAGE_ID ? mergedReg : p
		);
	}
	return normalized.map((p, i) => ({ ...p, order: i }));
};

export {
	pageHasQuizOrPoints,
	normalizePages,
	resolvePlayQuizId,
	collectUniqueQuizIdsFromElements,
	collectUniqueQuizIdsFromPages,
};
